import apiClient from './api';
import { ApiResponse } from '@/types';

export interface Submission {
  id: string;
  organization_id: string;
  conversation_id?: string;
  customer_id?: string;
  workflow_name: string;
  status: string;
  collected_data: Record<string, unknown>;
  customer_snapshot?: Record<string, unknown>;
  destination_type: string;
  external_id?: string;
  lead_score?: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface SubmissionListResponse {
  data: Submission[];
  meta: {
    totalItems: number;
    totalPages: number;
    perPage: number;
    currentPage: number;
  };
}

class SubmissionServiceClass {
  async getSubmissions(params?: {
    status?: string;
    workflow_name?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<SubmissionListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.workflow_name) searchParams.set('workflow_name', params.workflow_name);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const queryString = searchParams.toString();
    const url = `/submissions${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<any>(url);
    // Backend paginated() returns { data: [...], meta: {...} } at top level
    const body = response.data || {};
    return {
      data: body.data || [],
      meta: body.meta || { totalItems: 0, totalPages: 0, perPage: 25, currentPage: 1 },
    };
  }

  async getSubmission(id: string): Promise<Submission> {
    const response = await apiClient.get<ApiResponse<Submission>>(`/submissions/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to load submission');
  }

  async updateStatus(id: string, status: string): Promise<Submission> {
    const response = await apiClient.patch<ApiResponse<Submission>>(`/submissions/${id}/status`, { status });
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update submission status');
  }
}

export const submissionService = new SubmissionServiceClass();
